import {
  Box,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { FormikProps } from 'formik';
import React, { useMemo, useState } from 'react';

import { ReportingPropertyConfig } from './ReportingConfig';
import { FormFieldLabel } from 'packages/common-frontend/components/FormField';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import { MultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

const ReportTypeSelectOptions = CreditAgreement.DomainEntity.ReportConfigTypeIterable.filter(
  (type) => type !== 'facilityTrialBalance' && type !== 'loanTrialBalance',
).map((type) => ({ label: type, value: type }));

type ReportContainerProps = {
  organizationId: string;
  creditAgreementId: string;
  reportingProperty: CreditAgreement.DomainEntity.ReportingProperty;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  columnWidth: string;
  headersLength: number;
  propertyIdx: number;
  basePropertyKey: string;
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[];
  // Whether the mutation (edit/delete) column is shown. Kept in sync with the
  // table header so a read-only view (no Action column) stays column-aligned.
  canMutate: boolean;
};
const ReportContainer = ({
  organizationId,
  creditAgreementId,
  reportingProperty,
  wipConfig,
  fullPropertyKey,
  columnWidth,
  headersLength,
  propertyIdx,
  basePropertyKey,
  bankAccounts,
  canMutate,
}: ReportContainerProps) => {
  const {
    name,
    reportConfigTypes,
    description,
    _note_,
    _isWrittenIn_,
  } = reportingProperty;

  const [isOpen, setIsOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<
    CreditAgreement.DomainEntity.ReportConfigType[]
  >(reportConfigTypes);
  const [titleText, setTitleText] = useState(reportConfigTypes.join(', '));

  const handleChange = (
    newValue: CreditAgreement.DomainEntity.ReportConfigType,
  ) => {
    const index = selectedOptions.indexOf(newValue);

    if (index >= 0) {
      // only remove option if multiple are selected
      if (selectedOptions.length === 1) {
        return selectedOptions;
      }

      selectedOptions.splice(index, 1);
      setSelectedOptions(selectedOptions);
    } else {
      selectedOptions.push(newValue);
    }

    setTitleText(selectedOptions.join(', '));

    return selectedOptions;
  };

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700">
        <Td w={columnWidth}>{name}</Td>
        <Td>{reportConfigTypes.join(', ')}</Td>
        <Td w={columnWidth}>{description || '-'}</Td>

        {wipConfig && (
          <>
            {/* Display data — shown whenever the WIP columns are present,
                including the read-only view. */}
            <Td w={columnWidth}>{_note_ || '-'}</Td>
            <Td w={columnWidth}>{getBooleanSymbol(_isWrittenIn_)}</Td>
            {/* Action column — only when mutation is allowed (kept in sync with
                the header so the row stays column-aligned in read-only). */}
            {canMutate && (
              <Td w={columnWidth}>
                <EditIconButton
                  ariaLabel="edit"
                  label="Edit"
                  onClick={(event) => {
                    setIsOpen(true);
                    if (event) {
                      event.stopPropagation();
                    }
                  }}
                />
                <DeleteArrayPropertyButton
                  wipConfig={wipConfig}
                  basePropertyKey={basePropertyKey}
                  idx={propertyIdx}
                />
              </Td>
            )}
          </>
        )}

        <Td w="fit-content">
          {isConfigOpen ? (
            <IconButton
              aria-label="close config"
              icon={<FiChevronUp />}
              onClick={() => setIsConfigOpen(false)}
              variant="ghost"
            />
          ) : (
            <IconButton
              aria-label="display config"
              icon={<FiChevronDown />}
              onClick={() => setIsConfigOpen(true)}
              variant="ghost"
            />
          )}
        </Td>
      </Tr>

      {isConfigOpen && (
        <ReportingPropertyConfig
          organizationId={organizationId}
          creditAgreementId={creditAgreementId}
          reportingProperty={reportingProperty}
          wipConfig={wipConfig}
          configPropertyKey={`${fullPropertyKey}._config_`}
          columnLength={headersLength + 1} // Add + 1 to include "config" icon
          bankAccounts={bankAccounts}
        />
      )}

      {canMutate && wipConfig && (
        <EditPropertyModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          wipConfig={wipConfig}
          basePropertyKey={fullPropertyKey}
          modalHeaderLabel="Reporting Mechanics"
        >
          {(
            localFormProps: FormikProps<CreditAgreement.DomainEntity.ReportingProperty>,
          ) => (
            <Box w="100%">
              <Flex w="95%" alignItems="center">
                <FormFieldLabel label="Report Type" labelWidth="44" />
                <MultiSelect<CreditAgreement.DomainEntity.ReportConfigType>
                  onChange={(val) => {
                    const selectedTypes = handleChange(val);
                    localFormProps.setFieldValue(
                      'reportConfigTypes',
                      selectedTypes,
                    );
                  }}
                  options={ReportTypeSelectOptions}
                  selectedOptions={selectedOptions}
                  titleText={titleText}
                />
              </Flex>
            </Box>
          )}
        </EditPropertyModal>
      )}
    </>
  );
};

const REPORTING_BASE_TABLE_HEADERS = ['Name', 'Report Type', 'Description'];
// Display columns shown alongside the base ones whenever a wipConfig is present
// (both the editable and the read-only view). 'Action' is added separately, only
// when mutation is allowed.
const REPORTING_WIP_DISPLAY_HEADERS = ['Note', 'Is Written in'];

type ReportingTableProps = {
  organizationId: string;
  creditAgreementId: string;
  reportingMechanics: CreditAgreement.DomainEntity.ReportingMechanics;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[];
};
export const ReportingTable = ({
  organizationId,
  creditAgreementId,
  reportingMechanics,
  wipConfig,
  basePropertyKey,
  bankAccounts,
}: ReportingTableProps) => {
  // A single source of truth for whether the mutation (Action) column shows —
  // used for both the header and each row so they stay column-aligned. In the
  // read-only view `isMutateAllowed` returns false for every path.
  const canMutate = !!wipConfig && wipConfig.isMutateAllowed(basePropertyKey);

  const { tableHeaders, columnWidth } = useMemo<{
    tableHeaders: string[];
    columnWidth: string;
  }>(() => {
    const headers: string[] = wipConfig
      ? [
          ...REPORTING_BASE_TABLE_HEADERS,
          ...REPORTING_WIP_DISPLAY_HEADERS,
          ...(canMutate ? ['Action'] : []),
        ]
      : REPORTING_BASE_TABLE_HEADERS;
    const width: string = `${Math.floor(95 / (headers.length + 1))}%`;
    return { tableHeaders: headers, columnWidth: width };
  }, [wipConfig, canMutate]);

  if (!reportingMechanics.length) {
    return <Text>There are currently no sections set up for Reporting.</Text>;
  }

  return (
    <Box id="reporting-table-container">
      {reportingMechanics.map((property, idx) => {
        const fullPropertyKey = `${basePropertyKey}[${[idx]}]`;
        return (
          <Box key={fullPropertyKey} mb="5">
            <Table
              style={{ tableLayout: 'fixed' }}
              borderWidth="1px"
              id={`reporting-table-${idx}`}
            >
              <Thead>
                <Tr>
                  {tableHeaders.map((header) => (
                    <Th width={columnWidth} key={header}>
                      {header}
                    </Th>
                  ))}
                  <Th width="5%" />
                </Tr>
              </Thead>

              <Tbody>
                <ReportContainer
                  creditAgreementId={creditAgreementId}
                  organizationId={organizationId}
                  reportingProperty={property}
                  wipConfig={wipConfig}
                  fullPropertyKey={fullPropertyKey}
                  columnWidth={columnWidth}
                  headersLength={tableHeaders.length}
                  propertyIdx={idx}
                  basePropertyKey={basePropertyKey}
                  bankAccounts={bankAccounts}
                  canMutate={canMutate}
                />
              </Tbody>
            </Table>
          </Box>
        );
      })}
    </Box>
  );
};
